
// Dashboard.js (updated version with tenant-based filtering)

import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from "react-helmet";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../../config/config';
import { useTenant } from '../../context/TenantContext';
import LoggingService from '../../services/LoggingService';
import '../../styles/Dashboard.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from 'react-modal';

const Dashboard = () => {
  const [triggers, setTriggers] = useState([]);
  const [filteredTriggers, setFilteredTriggers] = useState([]);
  const [selectedType, setSelectedType] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  // Add sorting states here
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');

  // Add sorting function here
  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    const sortedTriggers = [...filteredTriggers].sort((a, b) => {
      if (field === 'created_at' || field === 'last_modified') {
        return sortDirection === 'asc'
          ? new Date(a[field]) - new Date(b[field])
          : new Date(b[field]) - new Date(a[field]);
      }
      return sortDirection === 'asc'
        ? String(a[field]).localeCompare(String(b[field]))
        : String(b[field]).localeCompare(String(a[field]));
    });

    setFilteredTriggers(sortedTriggers);
  };

  const [isTypeDropdownVisible, setIsTypeDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { selectedTenant } = useTenant();

  const fetchTriggers = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.get(`${config.API_BASE_URL}/api/triggers`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-UIPATH-OrganizationUnitId': selectedTenant?.id
        },
      });

      const tenantFiltered = response.data.map(trigger => {
        if (trigger.type === 'whatsapp' || trigger.type === 'WhatsApp') {
          return { ...trigger, type: 'WhatsApp' };
        }
        return trigger;
      });

      setTriggers(tenantFiltered);
      setFilteredTriggers(
        selectedType === 'All' ? tenantFiltered : tenantFiltered.filter(t =>
          t.type === selectedType ||
          (selectedType === 'WhatsApp' && (t.type === 'whatsapp' || t.type === 'WhatsApp'))
        )
      );
    } catch (error) {
      console.error('Detailed fetch error:', {
        message: error.message,
        response: error.response?.data,
        config: error.config
      });
    }
  };

  const handleTypeFilter = (type) => {
    LoggingService.logAction("Filter Change", `Trigger type filter changed to: ${type}`);
    setSelectedType(type);
    setIsTypeDropdownVisible(false);
  };

  const handleStatusChange = async (triggerId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
      LoggingService.logAction("Status Change", `Changing trigger ${triggerId} status from ${currentStatus} to ${newStatus}`);

      const token = localStorage.getItem('userToken');
      await axios.patch(
        `${config.API_BASE_URL}/api/triggers/${triggerId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      LoggingService.logAction("Status Update Success", `Successfully updated trigger ${triggerId} status to ${newStatus}`);
      fetchTriggers();
    } catch (error) {
      LoggingService.logError("Status Update Error", `Failed to update trigger ${triggerId} status: ${error.message}`);
      console.error('Error updating status:', error);
    }
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [triggerToDelete, setTriggerToDelete] = useState(null);

  const openDeleteModal = (id, name) => {
    setTriggerToDelete({ id, name });
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (triggerToDelete) {
      try {
        LoggingService.logAction("Delete Attempt", `Attempting to delete trigger: ${triggerToDelete.name} (ID: ${triggerToDelete.id})`);

        const response = await axios.delete(`${config.API_BASE_URL}/api/triggers/${triggerToDelete.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` },
        });

        if (response.status === 200 || response.status === 204) {
          LoggingService.logAction("Delete Success", `Successfully deleted trigger: ${triggerToDelete.name}`);
          toast.success(`"${triggerToDelete.name}" trigger has been removed successfully`);
          fetchTriggers();
        } else {
          LoggingService.logError("Delete Failed", `Failed to delete trigger: ${triggerToDelete.name}`);
          toast.error('Failed to remove trigger. Please try again.');
        }
      } catch (error) {
        LoggingService.logError("Delete Error", `Error deleting trigger ${triggerToDelete.name}: ${error.message}`);
        console.error('Error deleting trigger:', error);
        toast.error('Failed to remove trigger. Please try again.');
      }
    }
    setIsDeleteModalOpen(false);
    setTriggerToDelete(null);
  };

  useEffect(() => {
    if (selectedTenant?.id) {
      LoggingService.logSystem("Dashboard Init", `Initializing dashboard for tenant: ${selectedTenant.name}`);
      fetchTriggers();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      LoggingService.logSystem("Dashboard Cleanup", "Dashboard component cleanup");
    };
  }, [selectedTenant?.id]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsTypeDropdownVisible(false);
    }
  };

  useEffect(() => {
    const filtered = triggers.filter(
      (trigger) =>
        (selectedType === 'All' || trigger.type === selectedType) &&
        trigger.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTriggers(filtered);
  }, [searchTerm, selectedType, triggers]);

  return (

    <div className="dashboard-container">
      <Helmet><title>Dashboard - Autonomie</title></Helmet>
      <header style={{ display: 'flex', gap: 4 }}>
        {/* <div className="search-container">
          <input
            type="text"
            placeholder="Search triggers by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div> */}

        <button className="add-trigger-button" onClick={() => navigate('/email-trigger')}> + Add New Email</button>
        <button className="add-trigger-button" onClick={() => navigate('/file-path-trigger')}> + Add New File Path</button>
        <button className="add-trigger-button" onClick={() => navigate('/linked-task-trigger')}> + Add New Linked Task</button>
        <button className="add-trigger-button" onClick={() => navigate('/whatsapp-trigger')}> + Add New WhatsApp</button>
      </header>
      <div className="trigger-table-container">
        <table className="trigger-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('id')} data-active={sortField === 'id'}>
                Id {sortField === 'id' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('name')} data-active={sortField === 'name'}>
                Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className="type-header" onClick={() => setIsTypeDropdownVisible(!isTypeDropdownVisible)} ref={dropdownRef}>
                Type
                {isTypeDropdownVisible && (
                  <div className="type-dropdown">
                    <div onClick={() => handleTypeFilter('All')}>All</div>
                    <div onClick={() => handleTypeFilter('Email')}>Email</div>
                    <div onClick={() => handleTypeFilter('FilePath')}>File Path</div>
                    <div onClick={() => handleTypeFilter('LinkedTask')}>Linked Task</div>
                    <div onClick={() => handleTypeFilter('WhatsApp')}>WhatsApp</div>
                  </div>
                )}
              </th>
              <th onClick={() => handleSort('status')} data-active={sortField === 'status'}>
                Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('created_by')} data-active={sortField === 'created_by'}>
                Created By {sortField === 'created_by' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('created_at')} data-active={sortField === 'created_at'}>
                Created At {sortField === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('last_modified')} data-active={sortField === 'last_modified'}>
                Last Modified {sortField === 'last_modified' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {/* {filteredTriggers.map((trigger) => (
            <tr key={trigger.id}>
              <td>{trigger.id}</td>
              <td>{trigger.name}</td>
              <td>{trigger.type}</td>
              <td>
                <button
                  className={`status-button ${trigger.status.toLowerCase()}`}
                  onClick={() => handleStatusChange(trigger.id, trigger.status)}
                >
                  {trigger.status}
                </button>
              </td>
              <td>{trigger.created_by}</td>
              <td>{new Date(trigger.created_at).toLocaleDateString()} {new Date(trigger.created_at).toLocaleTimeString()}</td>
              <td>{new Date(trigger.last_modified).toLocaleDateString()} {new Date(trigger.last_modified).toLocaleTimeString()}</td>
              <td>
                <button
                  className="edit-button"
                  onClick={() => {
                    if (trigger.type === 'Email') navigate(`/edit-email-trigger/${trigger.id}`);
                    if (trigger.type === 'FilePath') navigate(`/edit-file-path-trigger/${trigger.id}`);
                    if (trigger.type === 'LinkedTask') navigate(`/edit-linked-task-trigger/${trigger.id}`);
                  }}
                >Edit</button>
              </td>
              <td>
                <button
                  className="delete-button"
                  onClick={() => handleDelete(trigger.id)}
                >Remove</button>
              </td>
            </tr>
          ))} */}
            {filteredTriggers.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  No triggers found for the selected tenant.
                </td>
              </tr>
            ) : (
              filteredTriggers.map((trigger) => (
                <tr key={trigger.id}>
                  <td>{trigger.id}</td>
                  <td title={trigger.name.length > 20 ? trigger.name : ""}>{trigger.name}</td>
                  <td title={trigger.type}>{trigger.type}</td>
                  <td>
                    <button
                      className={`status-button ${trigger.status.toLowerCase()}`}
                      onClick={() => handleStatusChange(trigger.id, trigger.status)}
                    >
                      {trigger.status}
                    </button>
                  </td>
                  <td title={trigger.created_by}>{trigger.created_by}</td>
                  <td>{new Date(trigger.created_at).toLocaleDateString()} {new Date(trigger.created_at).toLocaleTimeString()}</td>
                  <td>{new Date(trigger.last_modified).toLocaleDateString()} {new Date(trigger.last_modified).toLocaleTimeString()}</td>
                  <td>
                    <button
                      className="edit-button"
                      onClick={() => {
                        if (trigger.type === 'Email') navigate(`/edit-email-trigger/${trigger.id}`);
                        if (trigger.type === 'FilePath') navigate(`/edit-file-path-trigger/${trigger.id}`);
                        if (trigger.type === 'LinkedTask') navigate(`/edit-linked-task-trigger/${trigger.id}`);
                        if (trigger.type === 'WhatsApp') navigate(`/edit-whatsapp-trigger/${trigger.id}`);
                      }}
                    >Edit</button>
                  </td>
                  <td>
                    <button
                      className="delete-button"
                      onClick={() => openDeleteModal(trigger.id, trigger.name)}
                    >Remove</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Add Modal at the end of the container div */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        className="delete-modal"
        overlayClassName="delete-modal-overlay"
      >
        <div className="delete-modal-content">
          <h3>Confirm Delete</h3>
          <p>Are you sure you want to remove "{triggerToDelete?.name}" trigger?</p>
          <div className="delete-modal-buttons">
            <button onClick={confirmDelete} className="confirm-delete-btn">Delete</button>
            <button onClick={() => setIsDeleteModalOpen(false)} className="cancel-delete-btn">Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Dashboard;
